import {FormValue, HiddenValueWhileLoading, TRANSLATE} from '@brunata-angular/util';
import {inject, Injectable} from '@angular/core';
import {
    AgentMeterType,
    AgentMeterTypeUsageAndUnit,
    MetersForAgentData,
} from './select-meters-for-agent.model';
import {ConvertableUnit} from '../../consumption-agents-overview/consumption-agent.model';
import {groupBy, uniq, uniqBy} from 'lodash-es';
import {BehaviorSubject, combineLatest, map, Observable, startWith} from 'rxjs';
import {ConsumptionAgentService} from '../../consumption-agent.service';
import {SelectMetersTableFormGroup} from './select-meters-table/select-meters-for-agent-table.model';
import {NonNullableFormBuilder} from '@angular/forms';
import {SelectableItem} from '@brunata-angular/ui';
import {DateTime} from 'luxon';
import {Sorting} from '@brunata-angular/ui';
import {MonitoringMeterBase} from '../../../monitoring-meter.model';

export type MetersForAgentColumn =
    | keyof FormValue<SelectMetersTableFormGroup>['filterFormGroup']
    | 'proBraLoc';

export type MetersForAgentSorting = Sorting & {
    column: MetersForAgentColumn;
};

@Injectable({providedIn: 'root'})
export class SelectMetersForAgentService {
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly translate = inject(TRANSLATE);
    private readonly consumptionAgentService = inject(ConsumptionAgentService);

    readonly compareGroups$ = new BehaviorSubject(false);

    // shared table state
    readonly page$ = new BehaviorSubject(1);
    readonly pageSize$ = new BehaviorSubject(10);
    readonly meterTableVisible$ = new BehaviorSubject(false);

    readonly tableFormGroup: SelectMetersTableFormGroup = this.formBuilder.group({
        selectedColumnsFormGroup: this.formBuilder.group({
            selectedGroup1: this.formBuilder.control<boolean>(true),
            selectedGroup2: this.formBuilder.control<boolean>(true),
            meterNo: this.formBuilder.control<boolean>(true),
            meterId: this.formBuilder.control<boolean>(false),
            meterModel: this.formBuilder.control<boolean>(false),
            placement: this.formBuilder.control<boolean>(false),
            usageAndUnit: this.formBuilder.control<boolean>(true),
            mountingDate: this.formBuilder.control<boolean>(true),
            dismountedDate: this.formBuilder.control<boolean>(false),
            address: this.formBuilder.control<boolean>(false),
            propertyNo: this.formBuilder.control<boolean>(false),
            branchNo: this.formBuilder.control<boolean>(false),
            locationNo: this.formBuilder.control<boolean>(false),
            consumerName: this.formBuilder.control<boolean>(false),
        }),
        filterFormGroup: this.formBuilder.group({
            selectedGroup1: this.formBuilder.control<SelectableItem[]>([
                {name: this.translate('common_selected'), selected: false},
                {name: this.translate('common_not_selected'), selected: false},
            ]),
            selectedGroup2: this.formBuilder.control<SelectableItem[]>([
                {name: this.translate('common_selected'), selected: false},
                {name: this.translate('common_not_selected'), selected: false},
            ]),
            usageAndUnit: this.formBuilder.control<SelectableItem[]>([]),
            meterModel: this.formBuilder.control<SelectableItem[]>([]),
            meterNo: this.formBuilder.control<string>(''),
            meterId: this.formBuilder.control<string>(''),
            placement: this.formBuilder.control<string>(''),
            address: this.formBuilder.control<string>(''),
            propertyNo: this.formBuilder.control<string>(''),
            branchNo: this.formBuilder.control<string>(''),
            locationNo: this.formBuilder.control<string>(''),
            consumerName: this.formBuilder.control<string>(''),
            mountingDate: this.formBuilder.control<DateTime | null>(null),
            dismountedDate: this.formBuilder.control<DateTime | null>(null),
        }),
    });

    getMetersForAgentData(
        buildingNo: string,
        reportId?: string,
    ): Observable<HiddenValueWhileLoading<MetersForAgentData>> {
        return combineLatest([
            this.consumptionAgentService.getMetersForAgent(buildingNo, reportId),
            this.consumptionAgentService.getConvertableUnits(),
        ]).pipe(
            map(([metersForAgent, convertableUnits]) => ({
                loading: false,
                value: this.createMetersForAgentData(metersForAgent, convertableUnits),
            })),
            startWith({loading: true as const}),
        );
    }

    resetState(): void {
        this.page$.next(1);
        this.pageSize$.next(10);
        this.meterTableVisible$.next(false);
        this.tableFormGroup.reset();
    }

    private createMetersForAgentData(
        metersForAgent: MonitoringMeterBase[],
        convertableUnits: ConvertableUnit[],
    ): MetersForAgentData {
        const groupedMeters = groupBy(metersForAgent, 'meterSuperTypeNo');
        const meterTypesGroupedBySuperType = Object.entries(groupedMeters).map(
            ([meterSuperTypeNo, meters]) => ({
                meterSuperTypeNo,
                meterTypes: uniqBy(
                    meters.map(({unit, alloUnitType}) => ({
                        unit,
                        alloUnitType,
                    })),
                    meterType => `${meterType.unit}-${meterType.alloUnitType}`,
                ),
            }),
        );

        const superTypeToMeterTypeTranslationMap = new Map<string, AgentMeterTypeUsageAndUnit[]>(
            meterTypesGroupedBySuperType.map(({meterSuperTypeNo, meterTypes}) => [
                meterSuperTypeNo,
                meterTypes.map(({alloUnitType, unit}) => ({
                    unit,
                    alloUnitType,
                    usageAndUnit: this.meterTypeToUsageAndUnit({alloUnitType, unit}),
                })),
            ]),
        );

        const superTypeToConvertableUnitsTranslationMap = new Map<string, Map<string, string>>(
            meterTypesGroupedBySuperType.map(({meterSuperTypeNo, meterTypes}) => {
                const allUnits = new Set(
                    meterTypes.flatMap(({unit}) => {
                        const convertableUnit = convertableUnits.find(cu => cu.unitId === unit);

                        return [unit, ...(convertableUnit?.listOfConvertableUnits ?? [])];
                    }),
                );

                const unitTranslationMap = new Map(
                    Array.from(allUnits).map(unit => [
                        unit,
                        this.translate(`mappers_measurementUnit.${unit}`, 'online', 'common'),
                    ]),
                );

                return [meterSuperTypeNo, unitTranslationMap];
            }),
        );

        return {
            meters: metersForAgent.map(meter => ({
                ...meter,
                selectedGroup1: false,
                selectedGroup2: false,
                meterIdAsNumber: Number(meter.meterId),
                superTypeNoAsNumber: Number(meter.meterSuperTypeNo),
                unitNoAsNumber: Number(meter.unit),
                sequenceNoAsNumber: Number(meter.meterSequenceNo),
                translatedSuperType: this.translate(
                    `mappers_meterType.${meter.meterSuperTypeNo}`,
                    'online',
                    'common',
                ),
                translatedUnit: this.translate(
                    `mappers_measurementUnit.${meter.unit}`,
                    'online',
                    'common',
                ),
                usageAndUnit: this.meterTypeToUsageAndUnit(meter),
                isReplacement: metersForAgent.some(
                    ({replacedByMeterId}) => replacedByMeterId === meter.meterId,
                ),
                ...(meter.meterCriteriaMismatch && {
                    criteriaMismatchTranslation: this.translate('monitoring_unsupported_meter'),
                }),
            })),
            settings: {
                superTypeTranslationMap: new Map(
                    uniq(metersForAgent.map(meter => meter.meterSuperTypeNo)).map(superType => [
                        superType,
                        this.translate(`mappers_meterType.${superType}`, 'online', 'common'),
                    ]),
                ),
                superTypeToMeterTypeTranslationMap,
                superTypeToConvertableUnitsTranslationMap,
                unitToConvertableUnitsMap: new Map<string, string[]>(
                    convertableUnits.map(unit => [unit.unitId, unit.listOfConvertableUnits]),
                ),
            },
        };
    }

    private meterTypeToUsageAndUnit({unit, alloUnitType}: AgentMeterType): string {
        return `${this.translate(`mappers_allocationUnitMap.${alloUnitType}`, 'online', 'common')} - ${this.translate(`mappers_measurementUnit.${unit}`, 'online', 'common')}`;
    }
}
